import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SkillPublishNotice } from './skill-draft-badge';
import { SkillEditorDialogs } from './skill-editor-dialogs';
import { SkillEditorMain } from './skill-editor-main';
import { SkillEditorLoadError, SkillEditorLoading } from './skill-editor-states';
import {
  getRunHint,
  getSkillRunState,
  getStatusLabel,
  getVisibleFrontmatterErrors,
} from './skill-editor-status';
import { SkillEditorActions, SkillEditorToolbar } from './skill-editor-toolbar';
import { SkillFileNavigation } from './skill-file-navigation';
import { useSkillSaveErrorSnack } from './skill-save-error';
import { useEditorRoot } from './use-editor-root';
import { useSkillAutosaveTimer } from './use-skill-autosave';
import { useSkillDraftSession } from './use-skill-draft-session';
import { useSkillEditorActions } from './use-skill-editor-actions';
import { useSkillFileTree, useSkillTreeSelection } from './use-skill-file-tree';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    [theme.breakpoints.down('md')]: { flexDirection: 'column' },
  },
  root: {
    '& .MuiButton-root': { textTransform: 'none' },
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
}));

const SkillEditor = React.memo(function SkillEditor({ skillId }: { skillId: string }) {
  const { classes } = useStyles();
  const { focusField, getIsEditorMounted, setRootNode } = useEditorRoot();
  const selection = useSkillTreeSelection();
  const session = useSkillDraftSession({
    getIsEditorMounted,
    setExpandedFolders: selection.setExpandedFolders,
    setSection: selection.setSection,
    skillId,
  });
  const { canEdit, contentError, dirty, parsedContent, pendingAction, saveError, saving, skill } =
    session;
  const tree = useSkillFileTree({
    attachmentChanges: session.attachmentChanges,
    attachmentChangesRef: session.attachmentChangesRef,
    canEdit,
    editAttachmentChanges: session.editAttachmentChanges,
    flushEditors: session.flushEditors,
    getIsSaving: session.saveQueue.getIsRunning,
    savedPaths: session.savedPaths,
    saving,
    selection,
    skill,
    skillId,
  });
  const actions = useSkillEditorActions({ focusField, session, setSection: selection.setSection });
  const frontmatterErrors = useMemo(
    () => getVisibleFrontmatterErrors({ parsedContent, runAttempted: actions.runAttempted }),
    [parsedContent, actions.runAttempted]
  );

  useSkillAutosaveTimer({
    // A scope change's response carries the scope, which a save's response could overwrite.
    paused: actions.changingScope || actions.deleteOpen || tree.naming,
    session,
  });
  // Without a skill the page shows its own load error.
  useSkillSaveErrorSnack({
    error: skill ? saveError : null,
    onReload: session.reloadSkillClick,
    onRetry: session.retrySave,
    reloading: session.reloading,
  });

  if (!skill && !saveError && (session.queryLoading || Boolean(session.observedKey)))
    return <SkillEditorLoading />;

  if (session.queryError || !skill)
    return (
      <SkillEditorLoadError
        failed={Boolean(session.queryError || saveError)}
        retrying={session.reloading}
        onBack={actions.handleBack}
        onRetry={session.retryLoad}
      />
    );

  if (session.initializedKeyRef.current === null) return <SkillEditorLoading />;

  const outdated = Boolean(saveError?.outdated);
  const runHint = getRunHint({ contentError, parsedContent });
  const { publishBlocker, runBlocker, runsDraft, testFilesHint } = getSkillRunState({
    canEdit,
    dirty,
    outdated,
    runHint,
    skill,
  });
  const { deleting, discarding, duplicating, publishing } = actions;
  const draftBusy =
    !canEdit ||
    deleting ||
    duplicating ||
    publishing ||
    discarding ||
    Boolean(pendingAction) ||
    outdated;
  const skillName = (parsedContent.ok && parsedContent.frontmatter.name) || 'Untitled skill';

  return (
    <div ref={setRootNode} className={classes.root}>
      <Helmet>
        <title>{skillName}</title>
      </Helmet>

      <SkillEditorToolbar
        canEdit={canEdit}
        initiallyEditingName={!skill.name && canEdit}
        name={parsedContent.ok ? parsedContent.frontmatter.name : skill.name}
        nameEditable={canEdit && parsedContent.ok}
        scope={skill.scope}
        scopeLocked={
          !canEdit || deleting || actions.changingScope || actions.scopePending || outdated
        }
        skillId={skill.id}
        title={skillName}
        onBack={actions.handleBack}
        onNameSave={session.handleTitleSave}
        onScopeChange={actions.changeScope}
      >
        <SkillEditorActions
          canEdit={canEdit}
          deleteDisabled={!canEdit || deleting || duplicating}
          discardDisabled={draftBusy}
          duplicateDisabled={duplicating || deleting || Boolean(pendingAction)}
          publishBlocker={publishBlocker}
          publishDisabled={draftBusy || Boolean(publishBlocker)}
          publishLabel={skill.isPublished ? 'Publish Changes' : 'Publish'}
          publishing={publishing || pendingAction === 'publish'}
          runBlocker={runBlocker}
          runDisabled={deleting || duplicating || outdated || Boolean(testFilesHint)}
          runLabel={runsDraft ? 'Test' : 'Run'}
          running={pendingAction === 'run'}
          onDelete={actions.openDelete}
          onDiscard={
            canEdit && skill.isPublished && skill.hasUnpublishedChanges ? actions.openDiscard : null
          }
          onDuplicate={actions.handleDuplicate}
          onPublish={runsDraft ? actions.handlePublish : null}
          onRun={actions.handleRun}
        />
      </SkillEditorToolbar>
      {canEdit && (
        <SkillPublishNotice
          hasUnpublishedChanges={skill.hasUnpublishedChanges}
          isPublished={skill.isPublished}
        />
      )}
      <div className={classes.body}>
        <SkillFileNavigation
          attachmentChanges={session.attachmentChanges}
          canEdit={canEdit}
          skill={skill}
          tree={tree}
          onAttachmentChange={session.editAttachmentChanges}
        />
        <SkillEditorMain
          attachmentChanges={session.attachmentChanges}
          bodyEditorRef={session.bodyEditorRef}
          canEdit={canEdit}
          contentError={contentError}
          dirty={dirty}
          editorGeneration={session.editorGeneration}
          frontmatterErrors={frontmatterErrors}
          parsedContent={parsedContent}
          reloading={session.reloading}
          runHint={runHint}
          saving={saving}
          selectedContentType={tree.selectedContentType}
          selectedItem={tree.selectedItem}
          selectedItemId={tree.selectedItemId}
          selectedName={tree.selectedName}
          selectedTextFile={tree.selectedTextFile}
          skill={skill}
          statusLabel={getStatusLabel({ contentError, dirty, saveError, saving })}
          textFileEditorRef={session.textFileEditorRef}
          onAttachmentChange={session.editAttachmentChanges}
          onBodyChange={session.handleBodyChange}
          onEditorKeyDown={session.handleEditorKeyDown}
          onFrontmatterChange={session.handleFrontmatterChange}
          onReload={session.reloadSkillClick}
          onReplaceCopy={tree.selectReplacedCopy}
          onTextFileChange={tree.changeTextFile}
        />
      </div>
      <SkillEditorDialogs
        deleteOpen={actions.deleteOpen}
        discardOpen={actions.discardOpen}
        isPublished={skill.isPublished}
        isShared={skill.scope !== Apollo.AgentSkillScope.Personal}
        publishOpen={actions.publishConfirmOpen}
        skillName={skillName}
        onCloseDelete={actions.closeDelete}
        onCloseDiscard={actions.closeDiscard}
        onClosePublish={actions.closePublishConfirm}
        onConfirmDelete={actions.deleteSkill}
        onConfirmDiscard={actions.discardChanges}
        onConfirmPublish={actions.confirmPublish}
      />
    </div>
  );
});

/**
 * The skill editor. Keyed by skill: Duplicate opens the copy on this same route, and none of
 * the previous skill's editor state (its tree, folders, pending changes) may carry over.
 */
export const SkillEditorPage = React.memo(function SkillEditorPage(): JSX.Element {
  const { skillId = '' } = useParams();
  return <SkillEditor key={skillId} skillId={skillId} />;
});
